/* ==========================================================================
   NHD - Evergreen lane - The Truth About Cholesterol - OPT-IN PAGE
   Theme asset. Every query is scoped to #nhdChevOptin, so nothing in this
   file can reach theme markup.

   This file does NOT touch the opt-in form. ActiveCampaign's own embed
   renders it and AC's own script submits it. No tokens, no relay, no
   simulated submit. If the form needs restyling, that happens in AC.

   What is left here is presentation and plumbing:
     - the full-bleed escape fallback for browsers without :has()
     - the animated heart-rhythm rule under each section heading
     - smooth in-page scrolling
     - the click-to-load trailer facade
     - the sticky CTA
     - scroll reveal
     - UTM capture, so the watch page can still put them on the cart
   ========================================================================== */
(function () {
  'use strict';

  var root = document.getElementById('nhdChevOptin');
  if (!root) return;

  var TRAILER = (root.dataset.trailerYt || '').trim();
  var YT_ID = /^[A-Za-z0-9_-]{11}$/;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  root.classList.add('js-ecg');

  /* ------------------------------------------------------ full bleed
     The stylesheet handles this with :has(). This is the fallback for
     browsers without :has() support, and belt-and-braces for any theme
     wrapper the selector list does not name. */
  var section = root.closest('.shopify-section');
  if (section) section.classList.add('nhd-chev-fullbleed');

  /* ---------------------------------------------------------------- UTMs
     Captured on first landing and held for the session. ActiveCampaign
     redirects to the watch page after a successful submit, and that
     redirect is a static URL configured in AC, so it cannot carry the
     visitor's UTMs by itself. The watch page reads them back out of
     sessionStorage and stamps them onto the cart. */
  var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content',
                  'utm_term', 'gclid', 'fbclid', '_ef_transaction_id'];

  function store(k, v) { try { sessionStorage.setItem(k, v); } catch (e) {} }

  var q = new URLSearchParams(window.location.search);
  UTM_KEYS.forEach(function (k) {
    var v = q.get(k);
    if (v) store('nhd_chev_' + k, v);
  });

  /* NOTHING in this file touches ActiveCampaign's form, its submit, or its
     callbacks. An earlier version wrapped window._show_thank_you to record
     that a visitor had opted in. That is the one place this file could have
     interfered with AC's redirect, and a cosmetic greeting on the next page
     is not worth that risk, so it is gone.

     To have the watch page greet the visitor as opted in, append ?access=1
     to the redirect URL configured in ActiveCampaign. No code change. */

  /* --------------------------------------------------------- ECG rule */
  var RULE =
    '<svg viewBox="0 0 124 22" aria-hidden="true" focusable="false">' +
      '<circle class="halo" cx="6" cy="11" r="9"/>' +
      '<circle class="node" cx="6" cy="11" r="4.6"/>' +
      '<path class="base" d="M13,11 H56 l3,-1 l3,1 l4,-9 l5,17 l5,-12 l3,4 h7 H120"/>' +
      '<path class="scan" d="M13,11 H56 l3,-1 l3,1 l4,-9 l5,17 l5,-12 l3,4 h7 H120"/>' +
    '</svg>';

  root.querySelectorAll('.sec-head h2, .gap h2, .hour h2, .statin h2, .stage h2, .story h2, .final h2, .objection h2')
    .forEach(function (h) {
      if (h.closest('.form-card')) return;   /* form headings are not sections */
      var span = document.createElement('span');
      span.className = 'ecg-rule';
      span.setAttribute('aria-hidden', 'true');
      span.innerHTML = RULE;
      h.insertAdjacentElement('afterend', span);
    });

  /* ------------------------------------------------------ smooth anchors
     html{scroll-behavior:smooth} was dropped: it is a global rule and this
     page no longer owns the document. Handled here instead. */
  function scrollTo(el, focusFirst) {
    el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
    if (focusFirst) {
      var first = el.querySelector('input:not([type=hidden])');
      if (first) setTimeout(function () { first.focus({ preventScroll: true }); }, 420);
    }
  }

  root.querySelectorAll('a[href^="#"]').forEach(function (a) {
    a.addEventListener('click', function (ev) {
      var id = a.getAttribute('href').slice(1);
      if (!id) return;
      var target = root.querySelector('#' + CSS.escape(id));
      if (!target) return;
      ev.preventDefault();
      scrollTo(target, target.classList.contains('form-card'));
    });
  });

  /* --------------------------------------------------- trailer facade
     Click-to-load. Nothing is requested from YouTube and no YouTube
     cookie is set until the visitor presses play. With no id wired the
     frame drops its play affordance and becomes a CTA into the form, so
     the page never shows a broken player. */
  var frame = root.querySelector('#trailer');
  if (frame) {
    frame.setAttribute('role', 'button');
    frame.setAttribute('tabindex', '0');

    var act;
    if (YT_ID.test(TRAILER)) {
      act = function () {
        if (frame.classList.contains('is-playing')) return;
        var f = document.createElement('iframe');
        f.src = 'https://www.youtube-nocookie.com/embed/' + TRAILER +
                '?autoplay=1&rel=0&modestbranding=1&playsinline=1';
        f.setAttribute('allow', 'accelerometer; autoplay; encrypted-media; picture-in-picture');
        f.setAttribute('allowfullscreen', '');
        f.setAttribute('title', 'The Truth About Cholesterol, preview');
        frame.appendChild(f);
        frame.classList.add('is-playing');
      };
    } else {
      frame.classList.add('no-video');
      act = function () {
        var card = root.querySelector('#watch');
        if (card) scrollTo(card, true);
      };
    }

    frame.addEventListener('click', act);
    frame.addEventListener('keydown', function (ev) {
      if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); act(); }
    });
  }

  /* --------------------------------- sticky bar yields to either card */
  var bar = root.querySelector('.sticky-cta');
  var cards = root.querySelectorAll('.form-card');
  if (bar && cards.length && 'IntersectionObserver' in window) {
    var visible = 0;
    var fo = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) { visible += en.isIntersecting ? 1 : -1; });
      visible = Math.max(0, visible);
      bar.classList.toggle('hide', visible > 0);
    }, { threshold: 0.2 });
    cards.forEach(function (c) { fo.observe(c); });
  }

  /* --------------------------------------------------- scroll reveal */
  var items = root.querySelectorAll('.rv');
  if (reduce || !('IntersectionObserver' in window)) {
    items.forEach(function (el) { el.classList.add('in'); });
  } else {
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) { en.target.classList.add('in'); io.unobserve(en.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.06 });
    items.forEach(function (el, i) {
      el.style.transitionDelay = (Math.min(i % 4, 3) * 70) + 'ms';
      io.observe(el);
    });
  }
})();